"use client";

import { formatAsDollars } from "@/app/directories/shop/lib/formatAsDollars";
import {
  type TypedTranslationFunction,
  useTranslations,
} from "@/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { PriceSwitch } from "@frontend/shared";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@frontend/shared/components/ui/tooltip";
import { cn } from "@frontend/shared/lib/utils";
import { cva } from "class-variance-authority";
import { CalculatingPricesPill } from "./CalculatingPricesPill";
import { PriceToggleSymbol } from "./priceToggleSymbol";

const priceToggleVariants = cva("w-full flex flex-col h-fit", {
  variants: {
    isCategoryProduct: {
      true: "lg:min-w-[386px] sm:min-w-full",
      false: "lg:min-w-[400px] sm:min-w-full",
    },
    isCardCarousel: {
      true: "w-full max-w-full lg:min-w-0 sm:min-w-0",
      false: "",
    },
    disabled: {
      true: "",
      false: "",
    },
    isSelfPayOnlyClient: {
      true: "justify-center",
      false: "justify-between",
    },
  },
  defaultVariants: {
    isCategoryProduct: false,
    isCardCarousel: false,
    isSelfPayOnlyClient: false,
  },
  compoundVariants: [
    {
      isCardCarousel: true,
      className: "!max-w-full",
    },
    {
      disabled: true,
      isCategoryProduct: false,
      isCardCarousel: false,
      className: "",
    },
    {
      disabled: true,
      isCategoryProduct: true,
      className: "",
    },
  ],
});

const priceTogglePriceVariants = cva(
  "flex w-full items-center gap-2 h-full lg:h-[58px]",
  {
    variants: {
      isSelfPayOnly: {
        true: "justify-end",
        false: "justify-between",
      },
    },
    defaultVariants: {
      isSelfPayOnly: false,
    },
  },
);

export function getCopyForHrtOptionsGrid(
  isInsured: boolean,
  noInsuranceProcedures: boolean,
  isPendingPricesMode: boolean,
  t: TypedTranslationFunction<"Shop">,
) {
  if (noInsuranceProcedures) {
    return null;
  }

  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs",
        isInsured ? "bg-blue-100 text-blue-800" : "bg-gray-200 text-gray-800",
      )}
    >
      {isInsured ? t("WithInsurance") : t("SelfPay")}
    </span>
  );
}

export function getCopyForPriceToggle(
  checked: boolean,
  isProductSelfPayOnly: boolean,
  isPendingPricesMode: boolean,
) {
  if (isProductSelfPayOnly) {
    return null;
  }
  return checked ? " with insurance" : " before insurance applied";
}

function generatePriceCopy(
  price,
  checked,
  isSelfPayOnlyClient,
  isProductSelfPayOnly,
  isPendingPricesMode,
  t,
  textColorClass = "text-black",
) {
  if (isSelfPayOnlyClient || isProductSelfPayOnly) {
    return (
      <p className={`font-suisse font-semibold text-xl ${textColorClass}`}>
        {formatAsDollars(price, t)}
      </p>
    );
  }

  // Only show "Price Pending" when the toggle is actually in the insured
  // state. When the user toggles off to self-pay we fall through to the
  // normal cash-price render below.
  if (checked && isPendingPricesMode) {
    return (
      <p
        className={`font-suisse font-semibold text-[20px] ${textColorClass}/40`}
      >
        {t("PricePending")}
      </p>
    );
  }

  return (
    <p className={`font-suisse font-semibold text-xl ${textColorClass}`}>
      {formatAsDollars(price, t)}
    </p>
  );
}

function generatePriceCopyHeader(
  checked,
  isSelfPayOnlyClient,
  isProductSelfPayOnly,
  isPendingPricesMode,
  t,
  textColorClass = "text-black",
) {
  // Possible copy options: "Self pay", "Insured"
  if (isSelfPayOnlyClient || isProductSelfPayOnly) {
    return (
      <p className={`font-suisse font-base text-md ${textColorClass}`}>
        {t("SelfPay")}
      </p>
    );
  }

  if (checked) {
    return (
      <p className={`font-suisse font-base text-md ${textColorClass}`}>
        {t("Insured")}
      </p>
    );
  }

  return (
    <p className={`font-suisse font-base text-md ${textColorClass}`}>
      {t("SelfPay")}
    </p>
  );
}

export const PriceToggle = ({
  checked,
  onCheckedChange,
  disabled,
  price,
  lowestComparisonPrice,
  hasMultipleHrts,
  isSelfPayOnlyClient,
  isProductSelfPayOnly,
  isCategoryProduct,
  isCardCarousel,
  preDiscountprice,
  pricesLoaded,
  priceToggleColor,
  isFetchingPrice,
  textColorClass = "text-black",
}) => {
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const t = useTranslations("Shop");

  if (isFetchingPrice) {
    return <CalculatingPricesPill />;
  }

  return (
    <div
      style={{ backgroundColor: priceToggleColor }}
      className={cn(
        priceToggleVariants({
          isCategoryProduct,
          isCardCarousel,
          isSelfPayOnlyClient,
          disabled: !pricesLoaded,
        }),
      )}
    >
      <div
        className={cn(
          priceTogglePriceVariants({
            isSelfPayOnly: isSelfPayOnlyClient,
          }),
        )}
      >
        <div className={cn("flex flex-col")}>
          {generatePriceCopy(
            price,
            checked,
            isSelfPayOnlyClient,
            isProductSelfPayOnly,
            isPendingPricesMode,
            t,
            textColorClass,
          )}
          {generatePriceCopyHeader(
            checked,
            isSelfPayOnlyClient,
            isProductSelfPayOnly,
            isPendingPricesMode,
            t,
            textColorClass,
          )}
        </div>

        {!isSelfPayOnlyClient && (
          <div className="flex items-center gap-4">
            {isProductSelfPayOnly ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <PriceSwitch
                        checked={checked}
                        onCheckedChange={onCheckedChange}
                        disabled
                      >
                        <PriceToggleSymbol checked={checked} />
                      </PriceSwitch>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("SelfPayOnlyProduct")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <PriceSwitch checked={checked} onCheckedChange={onCheckedChange}>
                <PriceToggleSymbol checked={checked} />
              </PriceSwitch>
            )}
          </div>
        )}

        {/* {!isSelfPayOnlyClient &&
          isProductSelfPayOnly &&
          pricesLoaded &&
          price !== 0 && (
            <Popover>
              <PopoverTrigger>
                <div className="w-[40px] h-[40px] flex rounded-full items-center justify-center p-[2px] cursor-pointer">
                  <div className="relative w-full flex rounded-full items-center justify-center h-full overflow-hidden">
                    <PriceToggleSymbol
                      isProductSelfPayOnly={isProductSelfPayOnly}
                    />
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent asChild>
                <div className="h-full flex flex-row bg-[#F3F3F3] rounded-[20px] px-4 py-2 gap-1">
                  <p className="text-xs font-medium">
                    {t("InsuranceDoesNotApplyToThisProduct")}
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          )} */}
      </div>
      {/*
      {!isCategoryProduct &&
        lowestComparisonPrice !== Number.POSITIVE_INFINITY &&
        !isSelfPayOnlyClient &&
        !isPendingPricesMode &&
        pricesLoaded && (
          <div className="h-full flex flex-row bg-[#F3F3F3] rounded-b-[20px] px-4 py-2 gap-1">
            <p className="text-xs font-mono">
              {formatAsDollars(lowestComparisonPrice, t)}
            </p>
            <p className="text-xs font-light">
              {checked ? ` ${t("WithSelfPay")}` : ` ${t("WithInsurance")} `}
            </p>
          </div>
        )}

      {preDiscountprice && isSelfPayOnlyClient && (
        <div className="h-full flex flex-row bg-[#F3F3F3] rounded-b-[20px] px-4 py-2 gap-1">
          <p className="text-xs font-medium">{t("RegularPrice")}</p>
          <p className="text-xs font-mono">
            {formatAsDollars(preDiscountprice, t)}
          </p>
        </div>
      )} */}
    </div>
  );
};
